import { useEffect, useCallback, useState } from 'react';
import { Track } from 'livekit-client';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as React from 'react';
import { MediaDeviceMenu } from '@livekit/components-react';
import { TrackToggle } from '@livekit/components-react';
import { useLocalParticipantPermissions, usePersistentUserChoices } from '@livekit/components-react';
import { useMediaQuery } from "@uidotdev/usehooks";
import { useMaybeLayoutContext } from '@livekit/components-react';
import { supportsScreenSharing } from '@livekit/components-core';
import { StartMediaButton } from '@livekit/components-react';
import { Icon } from "@iconify/react";
import { useLiveEvents } from "../../contexts/LiveEventsContext";
import { ChatToggle } from '@livekit/components-react';
import { CollaboratorsToggle } from './CollaboratorsToggle';


import { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  .lk-button {
    height: 44px;

    &:focus {
      outline: none !important;
      box-shadow: none !important;
    }
  }
`;

/** @public */
const trackSourceToProtocol = (source) => {
  // NOTE: this mapping avoids importing the protocol package as that leads to a significant bundle size increase
  switch (source) {
    case Track.Source.Camera:
      return 1;
    case Track.Source.Microphone:
      return 2;
    case Track.Source.ScreenShare:
      return 3;
    default:
      return 0;
  }
};

/** @public */

/**
 * The `ControlBar` prefab gives the user the basic user interface to control their
 * media devices (camera, microphone and screen share), open the `Chat` and leave the room.
 *
 * @remarks
 * This component is build with other LiveKit components like `TrackToggle`,
 * `DeviceSelectorButton`, `DisconnectButton` and `StartAudio`.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <ControlBar />
 * </LiveKitRoom>
 * ```
 * @public
 */
export function ControlBar({
  variation,
  controls,
  saveUserChoices = true,
  onDeviceError,
  recording = false,
  livekit = true,
  collaboratorInfo = null,
  videoEnabled = true,
  audioEnabled = true,
  onPressedRecord = () => { },
  showCollaborators = false,
  onCollaboratorsToggle = () => { },
  onVideoToggle = () => { },
  onAudioToggle = () => { },
  liveEvent = null,
  ...props
}) {
  const { activeUser } = useSelector((state) => state.users);
  const navigate = useNavigate();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isCollaboratorsOpen, setIsCollaboratorsOpen] = useState(showCollaborators);

  const layoutContext = useMaybeLayoutContext();
  console.log("!!! LAYOUT CONTEXT: ", layoutContext);
  useEffect(() => {
    if (layoutContext?.widget.state?.showChat !== undefined) {
      setIsChatOpen(layoutContext?.widget.state?.showChat);
    }
  }, [layoutContext?.widget.state?.showChat]);
  const isTooLittleSpace = useMediaQuery(`(max-width: ${isChatOpen ? 1000 : 760}px)`);

  useEffect(() => {
    console.log("!!! COLLABORATORS TOGGLE: ", isCollaboratorsOpen);
    onCollaboratorsToggle(isCollaboratorsOpen);
  }, [isCollaboratorsOpen]);

  useEffect(() => {
    setIsCollaboratorsOpen(showCollaborators);
  }, [showCollaborators]);

  const defaultVariation = isTooLittleSpace ? 'minimal' : 'verbose';
  variation ??= defaultVariation;

  const visibleControls = { record: true, collaborators: true, leave: true, ...controls };


  try {
    const localPermissions = useLocalParticipantPermissions();

    if (!localPermissions) {
      visibleControls.camera = false;
      visibleControls.chat = false;
      visibleControls.microphone = false;
      visibleControls.screenShare = false;
    } else {
      const canPublishSource = (source) => {
        return (
          localPermissions.canPublish &&
          (localPermissions.canPublishSources.length === 0 ||
            localPermissions.canPublishSources.includes(trackSourceToProtocol(source)))
        );
      };
      visibleControls.camera ??= canPublishSource(Track.Source.Camera);
      visibleControls.microphone ??= canPublishSource(Track.Source.Microphone);
      visibleControls.screenShare ??= canPublishSource(Track.Source.ScreenShare);
      visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
      visibleControls.collaborators = true;
    }
  } catch (error) {
    visibleControls.camera = true;
    visibleControls.microphone = true;
    visibleControls.record = false;
    visibleControls.screenShare = true;
    visibleControls.chat = true;
    visibleControls.collaborators = false;
  }

  const showIcon = React.useMemo(
    () => variation === 'minimal' || variation === 'verbose',
    [variation],
  );
  const showText = React.useMemo(
    () => variation === 'textOnly' || variation === 'verbose',
    [variation],
  );

  const browserSupportsScreenSharing = supportsScreenSharing();

  const [isScreenShareEnabled, setIsScreenShareEnabled] = useState(false);

  const onScreenShareChange = useCallback(
    (enabled) => {
      setIsScreenShareEnabled(enabled);
    },
    [setIsScreenShareEnabled],
  );

  const htmlProps = { className: 'lk-control-bar' };

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  const microphoneOnChange = useCallback(
    (enabled, isUserInitiated) =>
      isUserInitiated ? saveAudioInputEnabled(enabled) : null,
    [saveAudioInputEnabled],
  );

  const cameraOnChange = useCallback(
    (enabled, isUserInitiated) => {
      if (isUserInitiated) {
        onVideoToggle(enabled);
        saveVideoInputEnabled(enabled);
      }
    },
    [saveVideoInputEnabled],
  );

  return (
    <div {...htmlProps}>
      <GlobalStyle />
      {visibleControls.record && collaboratorInfo?.role === "host" && (
        <button className="lk-button"
          onClick={() => {
            console.log("Record button clicked");
            onPressedRecord();
          }}
          style={{ marginRight: "8px" }}
        >
          {/* Record button implementation goes here */}

          { recording ? (
            <Icon icon="mdi:record" style={{ width: "28px", height: "28px", color: "red", borderRadius: "50%", border: "2px solid red" }} />
          ) :
            (
              <Icon icon="mdi:record" style={{ width: "28px", height: "28px", color: "red" }} />
            )}

        </button>
      )}
      {visibleControls.screenShare && browserSupportsScreenSharing && (
        <TrackToggle
          source={Track.Source.ScreenShare}
          captureOptions={{ audio: true, selfBrowserSurface: 'include' }}
          showIcon={showIcon}
          onChange={onScreenShareChange}
          onDeviceError={(error) => onDeviceError?.({ source: Track.Source.ScreenShare, error })}
          style={{ marginRight: "8px" }}
        >
          {showText && (isScreenShareEnabled ? 'Stop screen share' : 'Share screen')}
        </TrackToggle>
      )}
      {visibleControls.microphone && (
        <div className="lk-button-group"
          style={{ marginRight: "8px" }}
        >
          <TrackToggle
            aria-pressed={true}
            source={Track.Source.Microphone}
            showIcon={showIcon}
            onChange={microphoneOnChange}
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
          >
            {showText && 'Microphone'}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="audioinput"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveAudioInputDeviceId(deviceId ?? 'default')
              }
            />
          </div>
        </div>
      )}
      {visibleControls.camera && (
        <div className="lk-button-group"
          style={{ marginRight: "8px" }}
        >
          <TrackToggle
            source={Track.Source.Camera}
            showIcon={showIcon}
            onChange={cameraOnChange}
            onDeviceError={(error) => {
              console.log("!!! Camera error: ", error);
              onDeviceError?.({ source: Track.Source.Camera, error })
            }
            }
          >
            {showText && 'Camera'}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="videoinput"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveVideoInputDeviceId(deviceId ?? 'default')
              }
            />
          </div>
        </div>
      )}
      {visibleControls.chat && (
        <ChatToggle>
          <Icon icon="mdi:chat" style={{ width: "24px", height: "24px" }} />
        </ChatToggle>
      )}
      {visibleControls.collaborators && (
        <CollaboratorsToggle
          onPressed={() => {
            setIsCollaboratorsOpen((prev) => !prev);
          }}
        />
      )}

      {/*visibleControls.settings && (
        <SettingsMenuToggle>
          Settings
        </SettingsMenuToggle>
      )*/}
      {visibleControls.leave && (
        <button className="lk-button"
          onClick={() => {
            if (activeUser?.user_type.match(/client/)) {
              navigate(`/client/live-event/edit/${liveEvent.public_id}#media-docs`);
            } else {
              navigate(`/live-event/edit/${liveEvent.public_id}#media-docs`);
            }

          }}
          style={{ marginRight: "8px" }}
        >
          <Icon icon="mdi:exit-to-app" style={{ width: "24px", height: "24px" }} />
        </button>
      )}
      {livekit &&
        <StartMediaButton />
      }
    </div>
  );
}
